import TYPES from '@/types';
import Vue from 'vue';

// Application
import UpdateInvestorProfileInvestmentExperienceCommand
  from '@/modules/flagship/investor-profile/investment-experience/application/commands/update-investor-profile-investment-experience-command';
import GetInvestorProfileQuery
  from '@/modules/flagship/investor-profile/investor-profile/application/queries/get-investor-profile-query';
import {
  GetAllianzAccountQuery,
} from '@/modules/my-investment/allianz/allianz-account/application/queries';

// Domain
import { InvestorProfileInvestmentExperienceDto }
  from '@/modules/flagship/investor-profile/investment-experience/domain/dtos/investor-profile-investment-experience-dto';
import Inject from '@/modules/shared/domain/di/inject';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';

type BasicOption = {
  key: string;
  label: string;
}

export default class EditUserProfilePersonalityInvestmentExperienceViewModel {
  @Inject(TYPES.UPDATE_INVESTOR_PROFILE_INVESTMENT_EXPERIENCE_COMMAND)
  // eslint-disable-next-line max-len
  private readonly update_investment_experience_command!: UpdateInvestorProfileInvestmentExperienceCommand;

  @Inject(TYPES.GET_INVESTOR_PROFILE_QUERY)
  private readonly get_investor_profile_query!: GetInvestorProfileQuery;

  @Inject(TYPES.GET_ALLIANZ_ACCOUNT_QUERY)
  private readonly get_allianz_account_query!: GetAllianzAccountQuery;

  @Inject(TYPES.NOTIFIER)
  readonly message_notifier!: MessageNotifier;

  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  private readonly view!: Vue;

  readonly i18n_namespace = 'components.user-profile.user-profile-tabs.edit-user-profile-personality.investment-experience';

  readonly customer_id = sessionStorage.getItem('user_id');

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  private readonly error_allianz_account_not_exists = `Error customer_id:${this.customer_id}, not exists`;

  selected_financial_concepts: string[] = [];

  selected_investor_characteristics: string[] = [];

  investment_experience_dto: InvestorProfileInvestmentExperienceDto = {
    investor_profile_id: '',
    investment_experience: {
      financial_concepts_answers: [],
      sophisticated_investor_characteristics_answers: [],
    },
  };

  investor_characteristics_options: Array<BasicOption> = [
    {
      key: this.translate('sophisticated_investor_characteristics_answers.investments_in_the_last_year_key'),
      label: this.translate('sophisticated_investor_characteristics_answers.investments_in_the_last_year_label'),
    },
    {
      key: this.translate('sophisticated_investor_characteristics_answers.income_in_the_last_years_key'),
      label: this.translate('sophisticated_investor_characteristics_answers.income_in_the_last_years_label'),
    },
    {
      key: this.translate('sophisticated_investor_characteristics_answers.i_do_not_comply_key'),
      label: this.translate('sophisticated_investor_characteristics_answers.i_do_not_comply_label'),
    },
  ];

  financial_concepts_options: Array<BasicOption> = [
    {
      key: this.translate('financial_concepts_answers.actions_key'),
      label: this.translate('financial_concepts_answers.actions_label'),
    },
    {
      key: this.translate('financial_concepts_answers.debt_instruments_key'),
      label: this.translate('financial_concepts_answers.debt_instruments_label'),
    },
    {
      key: this.translate('financial_concepts_answers.derivatives_key'),
      label: this.translate('financial_concepts_answers.derivatives_label'),
    },
    {
      key: this.translate('financial_concepts_answers.investment_companies_key'),
      label: this.translate('financial_concepts_answers.investment_companies_label'),
    },
    {
      key: this.translate('financial_concepts_answers.etfs_key'),
      label: this.translate('financial_concepts_answers.etfs_label'),
    },
    {
      key: this.translate('financial_concepts_answers.investment_currencies_key'),
      label: this.translate('financial_concepts_answers.investment_currencies_label'),
    },
    {
      key: this.translate('financial_concepts_answers.none_key'),
      label: this.translate('financial_concepts_answers.none_label'),
    },
  ];

  public constructor(view: Vue) {
    this.view = view;
  }

  get continue_btn_is_disabled() {
    return !this.selected_financial_concepts.length || !this.selected_investor_characteristics
      .length;
  }

  deactivateOptionsOnNoneFinancialConceptSelected = (selected_option: string) => {
    const none_option = this.translate('financial_concepts_answers.none_key');
    if (selected_option === none_option) {
      this.selected_financial_concepts = [none_option];
    } else {
      const index = this.selected_financial_concepts.findIndex((item) => item === none_option);
      if (index !== -1) this.selected_financial_concepts.splice(index, 1);
    }
  }

  deactivateOptionsOnNotComplyInvestorCharacteristicSelected = (selected_option: string) => {
    const none_option = this.translate('sophisticated_investor_characteristics_answers.i_do_not_comply_key');
    if (selected_option === none_option) {
      this.selected_investor_characteristics = [none_option];
    } else {
      // eslint-disable-next-line max-len
      const index = this.selected_investor_characteristics.findIndex((item) => item === none_option);
      if (index !== -1) this.selected_investor_characteristics.splice(index, 1);
    }
  }

  loadAllianzAccount = async () => {
    try {
      const allianz_account = await this.get_allianz_account_query
        .execute({ customer_id: this.customer_id });
      if (allianz_account.actions) this.selected_financial_concepts.push(this.translate('financial_concepts_answers.actions_key'));
      if (allianz_account.deb_instruments) this.selected_financial_concepts.push(this.translate('financial_concepts_answers.debt_instruments_key'));
      if (allianz_account.derivatives) this.selected_financial_concepts.push(this.translate('financial_concepts_answers.derivatives_key'));
      if (allianz_account.investment_companies) this.selected_financial_concepts.push(this.translate('financial_concepts_answers.investment_companies_key'));
      if (allianz_account.etfs) this.selected_financial_concepts.push(this.translate('financial_concepts_answers.etfs_key'));
      if (allianz_account.investment_currencies) this.selected_financial_concepts.push(this.translate('financial_concepts_answers.investment_currencies_key'));
      if (allianz_account.investments_in_the_last_year) this.selected_investor_characteristics.push(this.translate('sophisticated_investor_characteristics_answers.investments_in_the_last_year_key'));
      if (allianz_account.income_in_the_last_years) this.selected_investor_characteristics.push(this.translate('sophisticated_investor_characteristics_answers.income_in_the_last_years_key'));

      if (!this.selected_financial_concepts.length) this.selected_financial_concepts.push(this.translate('financial_concepts_answers.none_key'));
      if (!this.selected_investor_characteristics.length) this.selected_investor_characteristics.push(this.translate('sophisticated_investor_characteristics_answers.i_do_not_comply_key'));
    } catch (error) {
      if (error.message !== this.error_allianz_account_not_exists) {
        this.message_notifier.showErrorNotification(this.translate('errors.get_allianz_account_query'));
      }
    }
  }

  loadInvestorProfile = async () => {
    try {
      const investor_profile = await this.get_investor_profile_query.execute(false);
      this.investment_experience_dto.investor_profile_id = investor_profile.id;
      if (investor_profile.investment_experience !== null) {
        if (investor_profile.investment_experience!.financial_concepts_answers) {
          investor_profile.investment_experience!.financial_concepts_answers.forEach((item) => {
            this.selected_financial_concepts.push(this.translate(`financial_concepts_answers.${item}_key`));
          });
        }
        // eslint-disable-next-line max-len
        if (investor_profile.investment_experience!.sophisticated_investor_characteristics_answers) {
          investor_profile.investment_experience!.sophisticated_investor_characteristics_answers
            .forEach((item) => {
              this.selected_investor_characteristics.push(this.translate(`sophisticated_investor_characteristics_answers.${item}_key`));
            });
        }
      } else {
        await this.loadAllianzAccount();
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.load_investor_profile'));
    }
  }

  updateInformation = async () => {
    try {
      this.view.$emit('loadingStep', true);
      this.investment_experience_dto.investment_experience = {
        financial_concepts_answers: this.selected_financial_concepts,
        sophisticated_investor_characteristics_answers: this.selected_investor_characteristics,
      };
      await this.update_investment_experience_command.execute(this.investment_experience_dto);

      this.view.$emit('nextStep');
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.update_investor_profile'));
    } finally {
      this.view.$emit('loadingStep', false);
    }
  }

  initialize = async () => {
    this.view.$emit('loadingStep', true);
    await this.loadInvestorProfile();
    this.view.$emit('loadingStep', false);
  }
}
